import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';

function Commissions() {
  var [data, setData] = useState(null);
  var [range, setRange] = useState('month');
  var [loading, setLoading] = useState(true);

  useEffect(function() { loadData(); }, [range]);

  var loadData = async function() {
    setLoading(true);
    try {
      var res = await axios.get('/api/staff/reports/commissions?range=' + range);
      if (res.data?.success) setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  var fmt = function(n) { return '$' + Number(n || 0).toFixed(2); };
  var pct = function(n) { return Number(n || 0).toFixed(0) + '%'; };

  if (loading && !data) {
    return <div style={s.center}><p style={{ color: '#888', fontSize: 18 }}>Loading commission data...</p></div>;
  }

  var staff = data?.staff || [];
  var totals = data?.totals || {};
  var maxRevenue = Math.max(...staff.map(function(s) { return s.revenue; }), 1);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#fff', margin: '0 0 6px' }}>💰 Staff Commissions</h1>
          <p style={{ fontSize: 14, color: '#888', margin: 0 }}>Revenue, commissions, and tips by team member</p>
        </div>
        <div style={{ display: 'flex', gap: 4, background: '#1a1c2e', borderRadius: 10, padding: 4 }}>
          {[
            { k: 'today', l: 'Today' },
            { k: 'week', l: '7 Days' },
            { k: 'month', l: '30 Days' },
            { k: 'all', l: 'All Time' },
          ].map(function(t) {
            return (
              <button key={t.k} onClick={function() { setRange(t.k); }} style={{
                padding: '8px 18px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                background: range === t.k ? '#ff6b2b' : 'transparent', color: range === t.k ? '#fff' : '#888',
              }}>{t.l}</button>
            );
          })}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 28 }}>
        <SummaryCard label="Total Revenue" value={fmt(totals.revenue)} color="#10b981" icon="📊" />
        <SummaryCard label="Total Commissions" value={fmt(totals.commission)} color="#ff6b2b" icon="💰" />
        <SummaryCard label="Total Tips" value={fmt(totals.tips)} color="#8b5cf6" icon="🎁" />
        <SummaryCard label="Services Done" value={totals.services || 0} color="#3b82f6" icon="✂️" />
      </div>

      {/* Staff Breakdown */}
      {staff.length === 0 ? (
        <div style={{ ...s.card, textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 18, color: '#fff', fontWeight: 600 }}>No commission data yet</div>
          <div style={{ fontSize: 14, color: '#888', marginTop: 8 }}>Complete some bookings to see staff earnings</div>
        </div>
      ) : (
        <>
          {/* Revenue Bar Chart */}
          <div style={s.card}>
            <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Revenue by Staff Member</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {staff.sort(function(a, b) { return b.revenue - a.revenue; }).map(function(member) {
                var barWidth = Math.max((member.revenue / maxRevenue) * 100, 4);
                return (
                  <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 100, fontSize: 14, fontWeight: 600, color: '#fff', flexShrink: 0 }}>
                      {member.name}
                    </div>
                    <div style={{ flex: 1, height: 32, background: '#1a1a30', borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
                      <div style={{
                        height: '100%', borderRadius: 8, background: 'linear-gradient(90deg, #ff6b2b, #ffab40)',
                        width: barWidth + '%', transition: 'width 0.6s', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 10,
                      }}>
                        {barWidth > 15 && <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{fmt(member.revenue)}</span>}
                      </div>
                      {barWidth <= 15 && (
                        <span style={{ position: 'absolute', left: barWidth + 2 + '%', top: '50%', transform: 'translateY(-50%)', fontSize: 12, fontWeight: 700, color: '#ffab40' }}>
                          {fmt(member.revenue)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed Table */}
          <div style={{ ...s.card, marginTop: 20, overflowX: 'auto' }}>
            <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Detailed Breakdown</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #1e1e35' }}>
                  {['Staff Member', 'Role', 'Rate', 'Services', 'Revenue', 'Commission', 'Tips', 'Total Earned'].map(function(h) {
                    return <th key={h} style={s.th}>{h}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {staff.sort(function(a, b) { return b.total_earned - a.total_earned; }).map(function(member) {
                  return (
                    <tr key={member.id} style={{ borderBottom: '1px solid #252840' }}>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: 10, background: '#ff6b2b',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontWeight: 800, fontSize: 14,
                          }}>{member.name.charAt(0)}</div>
                          <span style={{ fontWeight: 600, color: '#fff' }}>{member.name}</span>
                        </div>
                      </td>
                      <td style={s.td}><span style={s.badge}>{member.role || 'staff'}</span></td>
                      <td style={s.td}><span style={{ color: '#ffab40', fontWeight: 600 }}>{pct(member.commission_rate)}</span></td>
                      <td style={s.td}><span style={{ color: '#3b82f6', fontWeight: 600 }}>{member.services_completed}</span></td>
                      <td style={s.td}><span style={{ color: '#10b981', fontWeight: 700 }}>{fmt(member.revenue)}</span></td>
                      <td style={s.td}><span style={{ color: '#ff6b2b', fontWeight: 700 }}>{fmt(member.commission)}</span></td>
                      <td style={s.td}><span style={{ color: '#8b5cf6', fontWeight: 600 }}>{fmt(member.tips)}</span></td>
                      <td style={s.td}><span style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>{fmt(member.total_earned)}</span></td>
                    </tr>
                  );
                })}
                {/* Totals Row */}
                <tr style={{ borderTop: '2px solid #ff6b2b' }}>
                  <td style={{ ...s.td, fontWeight: 800, color: '#fff' }}>TOTAL</td>
                  <td style={s.td}></td>
                  <td style={s.td}></td>
                  <td style={{ ...s.td, fontWeight: 700, color: '#3b82f6' }}>{totals.services}</td>
                  <td style={{ ...s.td, fontWeight: 700, color: '#10b981' }}>{fmt(totals.revenue)}</td>
                  <td style={{ ...s.td, fontWeight: 700, color: '#ff6b2b' }}>{fmt(totals.commission)}</td>
                  <td style={{ ...s.td, fontWeight: 700, color: '#8b5cf6' }}>{fmt(totals.tips)}</td>
                  <td style={{ ...s.td, fontWeight: 800, color: '#fff', fontSize: 16 }}>{fmt((totals.commission || 0) + (totals.tips || 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Export */}
          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <button onClick={function() {
              var csv = 'Staff,Role,Rate,Services,Revenue,Commission,Tips,Total Earned\n';
              staff.forEach(function(m) {
                csv += [m.name, m.role, m.commission_rate + '%', m.services_completed, m.revenue.toFixed(2), m.commission.toFixed(2), m.tips.toFixed(2), m.total_earned.toFixed(2)].join(',') + '\n';
              });
              csv += ['TOTAL','','',totals.services,(totals.revenue||0).toFixed(2),(totals.commission||0).toFixed(2),(totals.tips||0).toFixed(2),((totals.commission||0)+(totals.tips||0)).toFixed(2)].join(',') + '\n';
              var blob = new Blob([csv], { type: 'text/csv' });
              var link = document.createElement('a');
              link.href = URL.createObjectURL(blob);
              link.download = 'commissions-' + range + '-' + new Date().toISOString().split('T')[0] + '.csv';
              link.click();
            }} style={{
              padding: '10px 20px', background: '#334155', border: 'none', borderRadius: 10,
              color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>📥 Export Commissions CSV</button>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color, icon }) {
  return (
    <div style={{
      background: '#12121f', borderRadius: 14, padding: 20,
      border: '1px solid #1e1e35',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ color: '#888', fontSize: 13 }}>{label}</span>
        <span style={{ fontSize: 20 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color }}>{value}</div>
    </div>
  );
}

var s = {
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' },
  card: { background: '#12121f', borderRadius: 16, padding: 24, border: '1px solid #1e1e35' },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1 },
  td: { padding: '12px 12px', fontSize: 14, color: '#ccc' },
  badge: { background: '#1a1a30', color: '#888', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' },
};

export default Commissions;
